import type { ReceiptDraft } from '@krp/client-runtime'

import { cartProblems, cartTotal, lineTotal, type Cart, type CartLine, type CartProblem } from './cart'
import { paymentMethodOf, paymentObjectOf } from './fiscalCodes'
import { fromMilli, toRubles } from './money'
import { planPayment, type PaymentChoice, type PaymentPlan } from './payment'

/** Чек для агента без ключа операции: ключ создаёт контроллер сессии на попытку (повтор того же чека — тот же ключ). */
export type ReceiptRequestDraft = Omit<ReceiptDraft, 'operationId'>

/**
 * Строка чека из строки корзины: предмет и способ расчёта — из профиля карточки по таблице договора (`fiscalCodes`), никаких
 * умолчаний. Единица остаётся в профиле/снимке: кассовый агент поля `unit` пока не принимает (CASH-CONTRACTS §1.3), а вес
 * идёт самим количеством (три знака). `null` — профиль не годится (до сюда не доходит: `cartProblems` его уже остановил).
 */
export function receiptItemOf(line: CartLine): ReceiptRequestDraft['items'][number] | null {
  const paymentObject = paymentObjectOf(line.profile?.paymentSubject ?? null)
  const paymentMethod = paymentMethodOf(line.profile?.paymentMethod ?? null)
  if (line.vat === null || paymentObject === null || paymentMethod === null) return null
  return {
    name: line.name,
    price: toRubles(line.priceKopecks ?? 0),
    quantity: fromMilli(line.quantityMilli),
    amount: toRubles(lineTotal(line) ?? 0),
    vat: line.vat,
    paymentObject,
    paymentMethod,
    // Маркированная позиция до сюда не доходит (блокируется до оплаты); кода маркировки у продажи нет.
    markingCode: null,
  }
}

export interface DraftInput {
  type: 'sale' | 'refund'
  cashier: { name: string; inn?: string; cashierId?: string }
  cart: Cart
  payment: PaymentChoice
  /** E-mail или телефон покупателя; с ним чек можно отправить электронно. */
  customerContact?: string
  /** `print` (по умолчанию) или `electronic` — только с адресом покупателя. */
  print?: 'print' | 'electronic'
}

export type DraftResult =
  | { ok: true; draft: ReceiptRequestDraft; totalKopecks: number; plan: PaymentPlan }
  | { ok: false; problems: readonly CartProblem[]; paymentText: string | null }

/**
 * Собрать чек для агента из корзины и выбранной оплаты. Если что-то мешает — вернуть все причины сразу (кассиру нужен полный список,
 * а не по одной за попытку). Ничего не отправляет и не угадывает: строка без цены или ставки НДС чек не пропускает.
 */
export function buildReceiptDraft(input: DraftInput): DraftResult {
  const problems = cartProblems(input.cart)
  const total = cartTotal(input.cart)
  const payment = problems.length === 0 ? planPayment(total, input.payment) : null
  if (problems.length > 0 || payment === null || !payment.ok) {
    return { ok: false, problems, paymentText: payment !== null && !payment.ok ? payment.text : null }
  }
  const items = input.cart.lines.map(receiptItemOf)
  const complete = items.filter((item): item is NonNullable<typeof item> => item !== null)
  if (complete.length !== items.length) {
    // Страховка: профиль строки не перевёлся в коды агента. Чек не собирается, умолчаний нет.
    return { ok: false, problems: [{ code: 'profile-incomplete', lineId: null, text: 'Кассовые реквизиты товара не переводятся в чек. Исправьте карточку товара.' }], paymentText: null }
  }
  const customer = input.customerContact?.trim()
  const draft: ReceiptRequestDraft = {
    type: input.type,
    cashier: input.cashier,
    items: complete,
    payments: payment.plan.payments,
    ...(customer === undefined || customer === '' ? {} : { customer: { emailOrPhone: customer } }),
    ...(input.print === undefined ? {} : { print: input.print }),
  }
  return { ok: true, draft, totalKopecks: total, plan: payment.plan }
}
